import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Search from '@mui/icons-material/Search';
import { DESKTOP_COLORS } from 'src/consts';
import { usePagination } from 'src/hooks/usePagination';
import { MerchantTitle } from 'app/MerchantTitle';
import { locationApi } from 'components/location/locationApi';
import { LocationsList } from 'components/location/LocationsList';
import { LocationsData } from 'components/location/model/Location';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { EmptyState } from 'lib/EmptyState';
import useDebounce from 'lib/helpers';
import logger from 'lib/logger';
import { IPoint, MuiMap } from 'lib/MuiMap';
import { ResponsiveLayout } from 'lib/Responsive';
import { SearchInput } from 'lib/SearchInput';
import { Throbber } from 'lib/Throbber';
import { isNumber } from 'lib/typeInference';
import { getCoordinatesSuccess, getLocations, GetLocationsOptions } from 'store/locations/locationActions';
import { ApplicationState } from 'store/store';
import { BaseRouteParams } from './routes';
import { Image } from 'lib/Image';

const PREFIX = 'LocationsPage';

const classes = {
    map: `${PREFIX}map`,
    desktopLocationsWrapper: `${PREFIX}desktopLocationsWrapper`,
    cardShadow: `${PREFIX}cardShadow`,
    locationsTitle: `${PREFIX}locationsTitle`,
    locationDescription: `${PREFIX}locationDescription`,
    searchInputBox: `${PREFIX}searchInputBox`,
    searchWrapper: `${PREFIX}searchWrapper`,
    searchInput: `${PREFIX}searchInput`,
    searchInputIcon: `${PREFIX}searchInputIcon`,
    locationsCard: `${PREFIX}locationsCard`
};

const StyledMobileContainer = styled(Box)(({ theme }) => ({
    overflowY: 'hidden',
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    [`& .${classes.searchInputBox}`]: {
        padding: theme.spacing(1)
    },
    [`& .${classes.map}`]: {
        width: '100%',
        height: '35vh',
        flexShrink: 0
    }
}));

const StyledLocationsList = styled(Box)(() => ({
    flex: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'auto',
        border: 'none'
    },
    '&::-webkit-scrollbar': {
        width: '2px',
        borderRight: '2px solid transparent'
    }
}));

const StyledDesktopContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    margin: 'auto',
    padding: theme.spacing(4.5, 0, 4.5, 0),
    flex: 1,
    [`& .${classes.map}`]: {
        width: '100%',
        height: '100%',
        flexShrink: 0
    },

    [`& .${classes.desktopLocationsWrapper}`]: {
        height: '100%',
        overflow: 'hidden',
        backgroundColor: DESKTOP_COLORS.LOCATIONS_BACKGROUND,
        '-webkit-mask-image': '-webkit-radial-gradient(white, black);'
    },

    [`& .${classes.cardShadow}`]: {
        boxShadow: theme.shadows[4],
        borderRadius: theme.shape.borderRadius,
        height: '100%'
    },

    [`& .${classes.locationsTitle}`]: {
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        fontWeight: 'bold',
        fontSize: theme.spacing(2.25),
        color: DESKTOP_COLORS.TEXT_PRIMARY
    },

    [`& .${classes.locationDescription}`]: {
        color: DESKTOP_COLORS.TEXT_PRIMARY,
        whiteSpace: 'pre-wrap'
    },

    [`& .${classes.searchWrapper}`]: {
        border: '1px solid rgba(0,0,0,0.3)',
        backgroundColor: theme.palette.common.white,
        color: DESKTOP_COLORS.TEXT_PRIMARY
    },

    [`& .${classes.searchInput}`]: {
        backgroundColor: theme.palette.common.white,
        color: DESKTOP_COLORS.TEXT_PRIMARY,
        '&::-webkit-input-placeholder': {
            color: DESKTOP_COLORS.TEXT_SECONDARY
        }
    },

    [`& .${classes.searchInputIcon}`]: {
        color: DESKTOP_COLORS.TEXT_PRIMARY
    },

    [`& .${classes.locationsCard}`]: {
        height: '100%',
        display: 'flex',
        overflowY: 'scroll',
        flexDirection: 'column',
        '&::-webkit-scrollbar': {
            display: 'none'
        }
    }
}));

function geoFindMe(onSuccess: (lat: number, lng: number) => void, onError: () => void) {
    function success(position: GeolocationPosition) {
        const { latitude, longitude } = position.coords;
        onSuccess(latitude, longitude);
    }

    if (!navigator.geolocation) {
        console.warn('Geolocation is not supported by your browser');
    } else {
        navigator.geolocation.getCurrentPosition(success, onError);
    }
}

export const LocationsPage: React.FC = () => {
    const dispatch = useDispatch();
    const { locations, isLoading, summary, lat, lng } = useSelector(
        (state: ApplicationState) => state.locations
    );
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');

    const { merchantId } = useParams<BaseRouteParams>();
    const [showLogo, setShowLogo] = React.useState<boolean>(true);
    const hasGeoLocation = React.useMemo(() => lat && lng, [lat, lng]);

    React.useEffect(() => {
        geoFindMe(
            (lattitude, longtitude) => {
                getCoordinatesSuccess(lattitude, longtitude)(dispatch);
            },
            () => {
                logger.warn('error during accessing coords');
            }
        );
    }, [dispatch]);

    // Debounce the search input so we do not spam the locations api
    // while the user might still be typing.
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    React.useEffect(() => {
        const getLocationOptions: GetLocationsOptions = hasGeoLocation
            ? {
                  coords: { lat: lat || 0, long: lng || 0 }
              }
            : {};
        if (debouncedSearchTerm !== '') {
            getLocationOptions.searchTerm = debouncedSearchTerm;
        }
        getLocations(getLocationOptions)(dispatch);
    }, [debouncedSearchTerm, dispatch, hasGeoLocation, lat, lng]);

    const mapCenter = React.useMemo(() => {
        if (lat && lng) {
            return { lat, lng };
        }
        return locations && locations[0] && locations[0].geo
            ? { lat: locations[0].geo[1], lng: locations[0].geo[0] }
            : { lat: 0, lng: 0 };
    }, [locations, lat, lng]);

    const handleSearchChange = React.useCallback((e: React.ChangeEvent<{ value: any }>) => {
        setSearchTerm(e.target.value);
    }, []);
    const clearInput = React.useCallback(() => {
        setSearchTerm('');
    }, []);

    const locationPoints: IPoint[] =
        locations.length === 0
            ? []
            : locations.map(location => ({
                  lat: location.geo[1],
                  lng: location.geo[0],
                  markerName: location.title
              }));
    const handleLogoFallbackError = React.useCallback(() => {
        setShowLogo(false);
    }, []);
    const renderMerchantTitle = React.useMemo(() => {
        if (showLogo) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center">
                    <Image
                        src={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/web_icon`}
                        onError={handleLogoFallbackError}
                        height="26px"
                    />
                </Box>
            );
        }
        return t('LOCATIONS_TITLE');
    }, [handleLogoFallbackError, merchantId, showLogo, t]);
    const {
        isLoading: isPaginationLoading,
        loadMoreItems,
        hasNext
    } = usePagination<LocationsData>({
        perPage: 25,
        initialState: summary
    });

    const handleLoadMoreItems = React.useCallback(
        () =>
            loadMoreItems(params => {
                const queryParams = { ...params };
                if (isNumber(lat) && isNumber(lng)) {
                    queryParams.lat = lat.toString();
                    queryParams.long = lng.toString();
                }
                return locationApi.getList({ ...queryParams });
            }),
        [lat, lng, loadMoreItems]
    );

    const boundTo = React.useMemo(
        () =>
            hasGeoLocation && locations[0]
                ? { lat: locations[0]?.geo[1], lng: locations[0]?.geo[0] }
                : undefined,
        [hasGeoLocation, locations]
    );

    return (
        <ResponsiveLayout
            renderMobileView={
                <StyledMobileContainer>
                    <MerchantTitle title={t('LOCATIONS_TITLE')} />
                    <MuiAppBar disableBack title={renderMerchantTitle} />
                    <MuiMap
                        width={0}
                        center={mapCenter}
                        className={classes.map}
                        options={{ keyboardShortcuts: false, disableDefaultUI: true }}
                        markers={locationPoints}
                        useCustomPin
                        boundAll={!hasGeoLocation}
                        boundTo={boundTo}
                    />
                    <SearchInput
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onClear={clearInput}
                        placeholder={t('LOCATIONS_SEARCHBAR')}
                        componentClasses={{
                            box: classes.searchInputBox
                        }}
                    />
                    {isLoading && <Throbber text={t('GENERAL_LOADING')} />}
                    {!isLoading && locations.length > 0 && (
                        <StyledLocationsList>
                            <LocationsList
                                loadMore={handleLoadMoreItems}
                                isLoading={isPaginationLoading}
                                hasNext={hasNext}
                                list={locations}
                                hasGeoLocation={!!hasGeoLocation}
                            />
                        </StyledLocationsList>
                    )}
                    {!isLoading && locations.length === 0 && (
                        <EmptyState
                            headerText={t('GENERAL_GENERIC_ERROR')}
                            paragraphText={t('LOCATION_NO_LOCATIONS_SEARCH')}
                        >
                            <Search style={{ fontSize: 80 }} />
                        </EmptyState>
                    )}
                </StyledMobileContainer>
            }
            renderDesktopView={
                <Box display="flex" flexDirection="column" height="100%">
                    <MerchantTitle title={t('LOCATIONS_TITLE')} />
                    <MuiAppBar disableBack title={renderMerchantTitle} />
                    <StyledDesktopContainer>
                        <div className={classes.cardShadow}>
                            <Paper className={classes.desktopLocationsWrapper}>
                                <Grid container spacing={0} height="100%">
                                    <Grid sx={{ overflowY: 'auto', height: '85vh' }} size={6}>
                                        <Box className={classes.locationsCard}>
                                            <Box paddingX={5.5} paddingTop={4} textAlign="center">
                                                <Typography variant="h6" className={classes.locationsTitle}>
                                                    {t('LOCATIONS_SELECT_TITLE')}
                                                </Typography>
                                                <Typography
                                                    className={classes.locationDescription}
                                                    variant="body1"
                                                >
                                                    {t('LOCATIONS_PAGE_DESCRIPTION')}
                                                </Typography>
                                            </Box>
                                            <Box paddingX={12.5} marginTop={2.5}>
                                                <SearchInput
                                                    value={searchTerm}
                                                    onChange={handleSearchChange}
                                                    onClear={clearInput}
                                                    placeholder={t('LOCATIONS_SEARCHBAR')}
                                                    componentClasses={{
                                                        input: classes.searchInput,
                                                        icon: classes.searchInputIcon,
                                                        wrapper: classes.searchWrapper,
                                                        box: classes.searchInputBox
                                                    }}
                                                />
                                            </Box>
                                            {!isLoading && locations.length > 0 && (
                                                <Box paddingX={1}>
                                                    <LocationsList
                                                        loadMore={handleLoadMoreItems}
                                                        isLoading={isPaginationLoading}
                                                        hasNext={hasNext}
                                                        list={locations}
                                                        hasGeoLocation={!!hasGeoLocation}
                                                    />
                                                </Box>
                                            )}
                                            {!isLoading && locations.length === 0 && (
                                                <EmptyState
                                                    headerText={t('GENERAL_GENERIC_ERROR')}
                                                    paragraphText={t('LOCATION_NO_LOCATIONS_SEARCH')}
                                                >
                                                    <Search style={{ fontSize: 80 }} />
                                                </EmptyState>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid size={6} sx={{ height: '85vh' }}>
                                        <MuiMap
                                            width={0}
                                            center={mapCenter}
                                            className={classes.map}
                                            options={{
                                                keyboardShortcuts: false,
                                                disableDefaultUI: true,
                                                zoomControl: true
                                            }}
                                            markers={locationPoints}
                                            useCustomPin
                                            boundAll={!hasGeoLocation}
                                            boundTo={boundTo}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </div>
                    </StyledDesktopContainer>
                </Box>
            }
        />
    );
};
